// @ts-expect-error
import injectCSS from './stylesheets/inject.scss';
// The module resolver is incapable of understanding this
// @ts-ignore
import Path from "epubjs/src/utils/path";
import Epub, { EpubCFI, } from "epubjs";
import { moveRangeEndsIntoTextNodes, PersistentRange, splitRangeToTextNodes } from "../common/lib/range";
import { FragmentSelectorConformsTo, isFragment } from "../common/lib/selector";
import { EPUBFindProcessor } from "./find";
import DOMView from "../common/dom-view";
import SectionRenderer from "./section-renderer";
import { closestElement, getContainingBlock } from "../common/lib/nodes";
import { StyleScoper } from "./lib/sanitize-and-render";
import PageMapping from "./lib/page-mapping";
import { lengthenCFI, shortenCFI } from "./cfi";
import { PaginatedFlow, ScrolledFlow } from "./flow";
import { DEFAULT_EPUB_APPEARANCE, RTL_SCRIPTS } from "./defines";
class EPUBView extends DOMView {
    _find = null;
    book;
    flow;
    flowMode;
    spreadMode;
    pageMapping;
    appearance;
    pageProgressionRTL;
    _lastResizeWidth = null;
    _lastResizeHeight = null;
    _sectionsContainer;
    _sectionRenderers = [];
    _rangeCache = new Map();
    _pageMappingJSON;
    constructor(options) {
        super(options);
        if (options.data.buf) {
            this.book = Epub(options.data.buf.buffer);
            delete this._options.data.buf;
        }
        else if (options.data.url) {
            this.book = Epub(options.data.url, {
                openAs: 'epub'
            });
        }
        else if (options.data.book) {
            this.book = options.data.book;
        }
        else {
            throw new Error('buf, url, or book is required');
        }
    }
    _getSrcDoc() {
        return '<!DOCTYPE html><html><body></body></html>';
    }
    getData() {
        return {
            book: this.book
        };
    }
    async _onInitialDisplay(viewState) {
        await this.book.opened;
        let cspMeta = this._iframeDocument.createElement('meta');
        cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
        cspMeta.setAttribute('content', this._getCSP());
        this._iframeDocument.head.prepend(cspMeta);
        this.pageProgressionRTL = this.book.packaging.metadata.direction === 'rtl';
        if (!this.pageProgressionRTL) {
            try {
                let locale = new Intl.Locale(this.book.packaging.metadata.language).maximize();
                this.pageProgressionRTL = locale.script ? RTL_SCRIPTS.has(locale.script) : false;
                if (this.pageProgressionRTL) {
                    console.log('Guessed RTL page progression from maximized locale: ' + locale);
                }
            }
            catch (e) {
                // Ignore
            }
        }
        let style = this._iframeDocument.createElement('style');
        style.innerHTML = injectCSS;
        this._iframeDocument.head.append(style);
        let swipeIndicatorContainer = this._iframeDocument.createElement('div');
        swipeIndicatorContainer.classList.add('swipe-indicators');
        let swipeIndicatorLeft = this._iframeDocument.createElement('div');
        swipeIndicatorLeft.classList.add('swipe-indicator-left');
        swipeIndicatorContainer.append(swipeIndicatorLeft);
        let swipeIndicatorRight = this._iframeDocument.createElement('div');
        swipeIndicatorRight.classList.add('swipe-indicator-right');
        swipeIndicatorContainer.append(swipeIndicatorRight);
        this._iframeDocument.body.append(swipeIndicatorContainer);
        this._sectionsContainer = this._iframeDocument.createElement('div');
        this._sectionsContainer.classList.add('sections');
        this._sectionsContainer.lang = this.book.packaging.metadata.language;
        this._sectionsContainer.hidden = true;
        this._iframeDocument.body.append(this._sectionsContainer);
        let styleScoper = new StyleScoper(this._iframeDocument);
        await this._displaySections(styleScoper);
        if (this._sectionRenderers.some(view => view.error) && await this._isEncrypted()) {
            this._options.onEPUBEncrypted();
            this._sectionsContainer.remove();
            return;
        }
        if (this._options.fontFamily) {
            this.setFontFamily(this._options.fontFamily);
        }
        this.setHyphenate(this._options.hyphenate ?? true);
        this._sectionsContainer.hidden = false;
        this.pageMapping = this._initPageMapping(viewState.savedPageMapping);
        this._initOutline();
        // Validate viewState and its properties
        // Also make sure this doesn't trigger _updateViewState
        this._setScale(viewState.scale || 1);
        if (viewState.flowMode) {
            this.setFlowMode(viewState.flowMode);
        }
        else {
            this.setFlowMode('paginated');
        }
        if (viewState.spreadMode) {
            this.setSpreadMode(viewState.spreadMode);
        }
        else {
            this.setSpreadMode(0 /* SpreadMode.None */);
        }
        if (viewState.appearance) {
            this.setAppearance(viewState.appearance);
        }
        else {
            this.setAppearance(DEFAULT_EPUB_APPEARANCE);
        }
        if (!viewState.cfi || viewState.cfi === '_start') {
            this.navigateToFirstPage();
        }
        else {
            let cfi = lengthenCFI(viewState.cfi);
            // Perform the navigation on the next frame, because apparently the split view layout might not have
            // settled yet
            await new Promise(resolve => requestAnimationFrame(resolve));
            this.navigate({ pageNumber: cfi }, { behavior: 'auto', offsetY: viewState.cfiElementOffset });
        }
        this._lastResizeWidth = this._iframeWindow.innerWidth;
        this._lastResizeHeight = this._iframeWindow.innerHeight;
        this._handleViewUpdate();
        // @ts-ignore
        this.book.archive.zip = null;
    }
    async _isEncrypted() {
        try {
            let xml = await this.book.archive.request('/META-INF/encryption.xml', 'text');
            return xml.includes('<EncryptedData');
        }
        catch (e) {
            return false;
        }
    }
    async _displaySection(section, styleScoper) {
        let renderer = new SectionRenderer({
            section,
            sectionsContainer: this._sectionsContainer,
            window: this._iframeWindow,
            document: this._iframeDocument,
            styleScoper,
        });
        await renderer.render(this.book.archive.request.bind(this.book.archive));
        renderer.body.lang = this.book.packaging.metadata.language;
        this._sectionRenderers[section.index] = renderer;
    }
    _displaySections(styleScoper) {
        return Promise.all(this.book.spine.spineItems
            // We should do this:
            // .filter(section => section.linear)
            // But we need to be sure it won't break anything
            .map(section => this._displaySection(section, styleScoper)));
    }
    _initPageMapping(json) {
        let mapping = null;
        if (json) {
            mapping = PageMapping.load(json, this);
        }
        if (!json || !mapping) {
            mapping = PageMapping.generate(this);
            json = mapping.save();
            if (window.dev) {
                mapping = PageMapping.load(json, this);
                if (!mapping) {
                    throw new Error('Failed to round-trip page mapping');
                }
            }
        }
        this._pageMappingJSON = json;
        return mapping;
    }
    _initOutline() {
        if (!this.book.navigation.toc.length) {
            return;
        }
        let navPath = new Path(this.book.packaging.navPath || this.book.packaging.ncxPath || '');
        let toOutlineItem = navItem => ({
            title: navItem.label,
            location: {
                href: navPath.resolve(navItem.href).replace(/^\//, '')
            },
            items: navItem.subitems?.map(toOutlineItem),
            expanded: true,
        });
        this._options.onSetOutline(this.book.navigation.toc.map(toOutlineItem));
    }
    getCFI(rangeOrNode) {
        let commonAncestorNode;
        if ('nodeType' in rangeOrNode) {
            commonAncestorNode = rangeOrNode;
        }
        else {
            commonAncestorNode = rangeOrNode.commonAncestorContainer;
        }
        let sectionContainer = closestElement(commonAncestorNode)?.closest('[data-section-index]');
        if (!sectionContainer) {
            return null;
        }
        let section = this.book.section(sectionContainer.getAttribute('data-section-index'));
        return new EpubCFI(rangeOrNode, section.cfiBase);
    }
    getRange(cfi, mount = false) {
        if (!this._sectionRenderers.length) {
            // The book isn't loaded yet -- don't spam the console
            return null;
        }
        let cfiString = cfi.toString();
        if (typeof cfi === 'string') {
            cfi = new EpubCFI(cfi);
        }
        let view = this._sectionRenderers[cfi.spinePos];
        if (!view) {
            console.error('Unable to find view for CFI', cfiString);
            return null;
        }
        if (!view.mounted && mount) {
            view.mount();
        }
        if (this._rangeCache.has(cfiString)) {
            return this._rangeCache.get(cfiString);
        }
        try {
            let range = cfi.toRange(view.container.ownerDocument, undefined, view.container);
            if (!range) {
                console.error('Unable to get range for CFI', cfiString);
                return null;
            }
            let persistentRange = new PersistentRange(range);
            this._rangeCache.set(cfiString, persistentRange);
            return persistentRange;
        }
        catch (e) {
            console.error('Unable to get range for CFI', cfiString, e);
            return null;
        }
    }
    toSelector(range) {
        range = moveRangeEndsIntoTextNodes(range);
        let cfi = this.getCFI(range);
        if (!cfi) {
            return null;
        }
        return {
            type: 'FragmentSelector',
            conformsTo: FragmentSelectorConformsTo.EPUB3,
            value: cfi.toString()
        };
    }
    toDisplayedRange(selector) {
        switch (selector.type) {
            case 'FragmentSelector': {
                if (selector.conformsTo !== FragmentSelectorConformsTo.EPUB3) {
                    throw new Error(`Unsupported FragmentSelector.conformsTo: ${selector.conformsTo}`);
                }
                if (selector.refinedBy) {
                    throw new Error('Refinement of FragmentSelectors is not supported');
                }
                let range = this.getRange(selector.value);
                if (!range) {
                    return null;
                }
                let sectionIndex = EPUBView.getContainingSectionIndex(range);
                if (sectionIndex === null || !this._sectionRenderers[sectionIndex].mounted) {
                    return null;
                }
                return range.toRange();
            }
            default:
                // No other selector types supported on EPUBs
                throw new Error(`Unsupported Selector.type: ${selector.type}`);
        }
    }
    _getHistoryLocation() {
        let cfi = this.flow.startCFI?.toString();
        if (!cfi)
            return null;
        return { pageNumber: cfi };
    }
    _keepPosition(block) {
        let cfiBefore = this.flow?.startCFI;
        let offsetBefore = this.flow?.startCFIOffsetY;
        let result = block?.();
        if (cfiBefore) {
            this.navigate({ pageNumber: cfiBefore.toString() }, {
                skipHistory: true,
                behavior: 'auto',
                offsetY: offsetBefore ?? undefined
            });
        }
        return result;
    }
    _navigateToSelector(selector, options = {}) {
        if (!isFragment(selector) || selector.conformsTo !== FragmentSelectorConformsTo.EPUB3) {
            console.warn("Not a CFI FragmentSelector", selector);
            return;
        }
        this.navigate({ pageNumber: selector.value }, options);
    }
    _getAnnotationFromRange(range, type, color) {
        range = moveRangeEndsIntoTextNodes(range);
        if (range.collapsed) {
            return null;
        }
        let text;
        if (type == 'highlight' || type == 'underline') {
            text = '';
            let lastSplitRange;
            for (let splitRange of splitRangeToTextNodes(range)) {
                if (lastSplitRange) {
                    let lastSplitRangeContainer = closestElement(lastSplitRange.commonAncestorContainer);
                    let lastSplitRangeBlock = lastSplitRangeContainer && getContainingBlock(lastSplitRangeContainer);
                    let splitRangeContainer = closestElement(splitRange.commonAncestorContainer);
                    let splitRangeBlock = splitRangeContainer && getContainingBlock(splitRangeContainer);
                    if (lastSplitRangeBlock !== splitRangeBlock) {
                        text += '\n\n';
                    }
                }
                text += splitRange.toString().replace(/\s+/g, ' ');
                lastSplitRange = splitRange;
            }
            text = text.trim();
            // If this annotation type wants text, but we didn't get any, abort
            if (!text) {
                return null;
            }
        }
        else {
            text = undefined;
        }
        let selector = this.toSelector(range);
        if (!selector) {
            return null;
        }
        let pageLabel = this.pageMapping.isPhysical && this.pageMapping.getPageLabel(range) || '';
        // Use the number of characters between the start of the section and the start of the selection range
        // to disambiguate the sortIndex
        let sectionContainer = closestElement(range.startContainer)?.closest('[data-section-index]');
        if (!sectionContainer) {
            return null;
        }
        let sectionIndex = parseInt(sectionContainer.getAttribute('data-section-index'));
        let offsetRange = this._iframeDocument.createRange();
        offsetRange.setStart(sectionContainer, 0);
        offsetRange.setEnd(range.startContainer, range.startOffset);
        let sortIndex = String(sectionIndex).padStart(5, '0') + '|' + String(offsetRange.toString().length).padStart(8, '0');
        return {
            type,
            color,
            sortIndex,
            pageLabel,
            position: selector,
            text
        };
    }
    // ***
    // Event handlers
    // ***
    _handleResize() {
        if (!this.flow || document.hidden
            || (this._iframeWindow.innerWidth === this._lastResizeWidth
                && this._iframeWindow.innerHeight === this._lastResizeHeight)) {
            return;
        }
        this._lastResizeWidth = this._iframeWindow.innerWidth;
        this._lastResizeHeight = this._iframeWindow.innerHeight;
        this._keepPosition();
        this._handleViewUpdate();
    }
    _getInternalLinkHref(link) {
        if (this._isExternalLink(link)) {
            return null;
        }
        let href = link.getAttribute('href');
        let section = this._sectionRenderers.find(view => view.container.contains(link))?.section;
        if (!section) {
            return null;
        }
        // This is a hack - we're using the URL constructor to resolve the relative path based on the section's
        // canonical URL, but it'll error without a host. So give it one!
        let url = new URL(href, new URL(section.canonical, 'https://www.example.com/'));
        return this.book.path.relative(url.pathname + url.hash);
    }
    _splitHref(href) {
        let [pathname, hash] = href.split('#');
        try {
            pathname = decodeURIComponent(pathname);
        }
        catch (e) { }
        if (hash) {
            try {
                hash = decodeURIComponent(hash);
            }
            catch (e) {
            }
        }
        return [pathname, hash ?? null];
    }
    _handlePointerOverInternalLink(link) {
        let element = this._getFootnoteTargetElement(link);
        if (element) {
            this._overlayPopupDelayer.open(link, () => {
                this._openFootnoteOverlayPopup(link, element);
            });
        }
        else {
            this._overlayPopupDelayer.close(() => {
                this._options.onSetOverlayPopup();
            });
        }
    }
    _handleInternalLinkClick(link) {
        // If link goes to footnote wrapped in an <aside>, open it in a popup instead of navigating
        let element = this._getFootnoteTargetElement(link);
        if (element && element.closest('aside')) {
            this._openFootnoteOverlayPopup(link, element);
            return;
        }
        let href = this._getInternalLinkHref(link);
        if (!href) {
            return;
        }
        this.navigate({ href });
    }
    _handleKeyDown(event) {
        let { key } = event;
        if (!event.shiftKey) {
            if (key == 'ArrowLeft') {
                if (this.pageProgressionRTL) {
                    this.flow.navigateToNextPage();
                }
                else {
                    this.flow.navigateToPreviousPage();
                }
                event.preventDefault();
                return;
            }
            if (key == 'ArrowRight') {
                if (this.pageProgressionRTL) {
                    this.flow.navigateToPreviousPage();
                }
                else {
                    this.flow.navigateToNextPage();
                }
                event.preventDefault();
                return;
            }
        }
        super._handleKeyDown(event);
    }
    _updateViewState() {
        let cfi;
        if (this.flow.startCFI) {
            cfi = shortenCFI(this.flow.startCFI.toString(true));
        }
        else if (this.flow.startRangeIsBeforeFirstMapping) {
            cfi = '_start';
        }
        let viewState = {
            scale: Math.round(this.scale * 1000) / 1000,
            cfi,
            cfiElementOffset: this.flow.startCFIOffsetY ?? undefined,
            savedPageMapping: this._pageMappingJSON,
            flowMode: this.flowMode,
            spreadMode: this.spreadMode,
            appearance: this.appearance,
        };
        this._options.onChangeViewState(viewState);
    }
    // View stats provide information about the view
    _updateViewStats() {
        let startRange = this.flow.startRange;
        let pageIndex = startRange && this.pageMapping.getPageIndex(startRange);
        let pageLabel = startRange && this.pageMapping.getPageLabel(startRange);
        let canNavigateToPreviousPage = this.flow.canNavigateToPreviousPage();
        let canNavigateToNextPage = this.flow.canNavigateToNextPage();
        let viewStats = {
            pageIndex: pageIndex ?? undefined,
            pageLabel: pageLabel ?? '',
            pagesCount: this.pageMapping.length,
            usePhysicalPageNumbers: this.pageMapping.isPhysical,
            canCopy: !!this._selectedAnnotationIDs.length || !(this._iframeWindow.getSelection()?.isCollapsed ?? true),
            canZoomIn: this.scale === undefined || this.scale < this.MAX_SCALE,
            canZoomOut: this.scale === undefined || this.scale > this.MIN_SCALE,
            canZoomReset: this.scale !== undefined && this.scale !== 1,
            canNavigateBack: this._history.canNavigateBack,
            canNavigateForward: this._history.canNavigateForward,
            canNavigateToFirstPage: canNavigateToPreviousPage,
            canNavigateToLastPage: canNavigateToNextPage,
            canNavigateToPreviousPage,
            canNavigateToNextPage,
            canNavigateToPreviousSection: this.canNavigateToPreviousSection(),
            canNavigateToNextSection: this.canNavigateToNextSection(),
            flowMode: this.flowMode,
            spreadMode: this.spreadMode,
        };
        this._options.onChangeViewStats(viewStats);
    }
    _handleViewUpdate() {
        super._handleViewUpdate();
        this.flow.invalidate();
    }
    _openFootnoteOverlayPopup(link, element) {
        let doc = document.implementation.createHTMLDocument();
        let cspMeta = this._iframeDocument.createElement('meta');
        cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
        cspMeta.setAttribute('content', this._getCSP());
        doc.head.prepend(cspMeta);
        let container = document.createElement('div');
        let current = element;
        let currentClone = current.cloneNode(true);
        while (!current.classList.contains('section-container')) {
            let parent = current.parentElement;
            if (!parent) {
                break;
            }
            let parentClone = parent.cloneNode(false);
            parentClone.appendChild(currentClone);
            currentClone = parentClone;
            current = parent;
        }
        container.appendChild(currentClone);
        container.querySelectorAll('a').forEach((link) => {
            link.removeAttribute('href');
        });
        doc.body.append(container);
        let content = new XMLSerializer().serializeToString(doc);
        let range = link.ownerDocument.createRange();
        range.selectNode(link);
        let domRect = range.getBoundingClientRect();
        let rect = [domRect.left, domRect.top, domRect.right, domRect.bottom];
        let css = '';
        for (let sheet of [...this._iframeDocument.styleSheets, ...this._iframeDocument.adoptedStyleSheets]) {
            for (let rule of sheet.cssRules) {
                css += rule.cssText + '\n\n';
            }
        }
        css += `
			:root {
				--content-scale: ${this.scale};
				--content-font-family: ${this._iframeDocument.documentElement.style.getPropertyValue('--content-font-family')};
			}
		`;
        let overlayPopup = {
            type: 'footnote',
            content,
            css,
            rect,
            ref: link
        };
        this._options.onSetOverlayPopup(overlayPopup);
    }
    _isFootnoteLink(link, target) {
        // Modeled on Calibre's heuristic
        // https://github.com/kovidgoyal/calibre/blob/87f4c08c16b07058dd25733eb5c30022246a66f2/src/pyj/read_book/footnotes.pyj#L32
        if (link.getAttributeNS('http://www.idpf.org/2007/ops', 'type') === 'noteref') {
            return true;
        }
        let roles = link.role?.split(' ') ?? [];
        if (roles.includes('doc-noteref') || roles.includes('doc-biblioref') || roles.includes('doc-glossref')) {
            return true;
        }
        if (roles.includes('doc-link')) {
            return false;
        }
        // Check if element has super/subscript alignment
        let elem = link;
        let remainingDepth = 3;
        while (elem && remainingDepth > 0) {
            let style = getComputedStyle(elem);
            if (!['inline', 'inline-block'].includes(style.display)) {
                break;
            }
            if (['sub', 'super', 'top', 'bottom'].includes(style.verticalAlign)) {
                return true;
            }
            elem = elem.parentElement;
            remainingDepth--;
        }
        // Check if it has a single child with super/subscript alignment
        if (link.innerText.trim() && link.children.length === 1) {
            let style = getComputedStyle(link.children[0]);
            if (['inline', 'inline-block'].includes(style.display)
                && ['sub', 'super', 'top', 'bottom'].includes(style.verticalAlign)) {
                return true;
            }
        }
        // Check if it has a link back to the original link
        let sectionIndex = link.closest('[data-section-index]')?.getAttribute('data-section-index');
        let section = sectionIndex && this.book.spine.get(sectionIndex);
        if (!section) {
            return false;
        }
        for (let linkInTarget of target.querySelectorAll('a')) {
            let linkInTargetHref = this._getInternalLinkHref(linkInTarget);
            if (!linkInTargetHref) {
                continue;
            }
            let [pathname, hash] = this._splitHref(linkInTargetHref);
            if (pathname === section.href && hash === link.id) {
                return true;
            }
        }
        return false;
    }
    _getFootnoteTargetElement(link) {
        let href = this._getInternalLinkHref(link);
        if (!href) {
            return null;
        }
        let [pathname, hash] = this._splitHref(href);
        if (!pathname || !hash) {
            return null;
        }
        let section = this.book.spine.get(pathname);
        if (!section) {
            return null;
        }
        let target = this._sectionRenderers[section.index].container
            .querySelector('[id="' + CSS.escape(hash) + '"]');
        if (!target) {
            return null;
        }
        let epubType = target.getAttributeNS('http://www.idpf.org/2007/ops', 'type');
        if (!epubType || !['footnote', 'rearnote', 'note'].includes(epubType)) {
            target = getContainingBlock(target) || target;
        }
        if (!this._isFootnoteLink(link, target)) {
            return null;
        }
        return target;
    }
    // ***
    // Setters that get called once there are changes in reader._state
    // ***
    // Unlike annotation, selection and overlay popups, find popup open state is determined
    // with .open property. All popup properties are preserved even when it's closed
    async setFindState(state) {
        let previousState = this._findState;
        this._findState = state;
        if (!state.active && previousState && previousState.active !== state.active) {
            console.log('Closing find popup');
            if (this._find) {
                this._find.cancel();
                this._find = null;
                this._handleViewUpdate();
            }
        }
        else if (state.active) {
            if (!previousState
                || previousState.query !== state.query
                || previousState.caseSensitive !== state.caseSensitive
                || previousState.entireWord !== state.entireWord
                || previousState.active !== state.active) {
                console.log('Initiating new search', state);
                this._find?.cancel();
                this._find = new EPUBFindProcessor({
                    view: this,
                    findState: { ...state },
                    onSetFindState: this._options.onSetFindState,
                });
                let startRange = (this.flow.startRange && new PersistentRange(this.flow.startRange)) ?? undefined;
                let onFirstResult = () => this.findNext();
                await this._find.run(startRange, onFirstResult);
            }
            else if (previousState && previousState.highlightAll !== state.highlightAll) {
                this._find.findState.highlightAll = state.highlightAll;
                this._renderAnnotations();
            }
        }
    }
    setFlowMode(flowMode) {
        if (flowMode == this.flowMode) {
            return;
        }
        this._keepPosition(() => {
            if (this.flow) {
                this.flow.destroy();
            }
            this.flowMode = flowMode;
            this.flow = new (flowMode == 'paginated' ? PaginatedFlow : ScrolledFlow)({
                view: this,
                iframe: this._iframe,
                onUpdateViewState: () => this._updateViewState(),
                onUpdateViewStats: () => this._updateViewStats(),
                onViewUpdate: () => this._handleViewUpdate(),
                onPushHistoryPoint: (transient) => {
                    this._pushHistoryPoint(transient);
                },
            });
            this.flow.setSpreadMode(this.spreadMode);
        });
        this._handleViewUpdate();
    }
    setSpreadMode(spreadMode) {
        if (spreadMode !== 0 /* SpreadMode.None */ && spreadMode !== 1 /* SpreadMode.Odd */) {
            throw new Error('Unsupported spread mode');
        }
        if (spreadMode == this.spreadMode) {
            return;
        }
        this._keepPosition(() => {
            this.spreadMode = spreadMode;
            this.flow?.setSpreadMode(spreadMode);
        });
        this._handleViewUpdate();
    }
    setAppearance(partialAppearance) {
        this._keepPosition(() => {
            let appearance = {
                ...DEFAULT_EPUB_APPEARANCE,
                ...partialAppearance
            };
            this.appearance = appearance;
            this._iframeDocument.documentElement.style.setProperty('--content-line-height-adjust', String(appearance.lineHeight));
            this._iframeDocument.documentElement.style.setProperty('--content-word-spacing-adjust', String(appearance.wordSpacing));
            this._iframeDocument.documentElement.style.setProperty('--content-letter-spacing-adjust', String(appearance.letterSpacing));
            this._iframeDocument.documentElement.classList.toggle('use-original-font', appearance.useOriginalFont);
            let pageWidth;
            switch (appearance.pageWidth) {
                case -1 /* PageWidth.Narrow */:
                    pageWidth = 'narrow';
                    break;
                case 0 /* PageWidth.Normal */:
                    pageWidth = 'normal';
                    break;
                case 1 /* PageWidth.Full */:
                    pageWidth = 'full';
                    break;
            }
            this._iframeDocument.documentElement.dataset.pageWidth = pageWidth;
        });
        this._handleViewUpdate();
    }
    setFontFamily(fontFamily) {
        this._iframeDocument.documentElement.style.setProperty('--content-font-family', fontFamily);
        this._renderAnnotations(true);
    }
    setHyphenate(hyphenate) {
        this._iframeDocument.documentElement.classList.toggle('hyphenate', hyphenate);
        this._renderAnnotations(true);
    }
    // ***
    // Public methods to control the view from the outside
    // ***
    async findNext() {
        console.log('Find next');
        if (this._find) {
            let processor = this._find;
            let result = await processor.next();
            if (result) {
                this.flow.scrollIntoView(result.range);
            }
            this._renderAnnotations();
        }
    }
    async findPrevious() {
        console.log('Find previous');
        if (this._find) {
            let processor = this._find;
            let result = await processor.prev();
            if (result) {
                this.flow.scrollIntoView(result.range);
            }
            this._renderAnnotations();
        }
    }
    _setScale(scale) {
        this._keepPosition(() => {
            this.scale = scale;
            this._iframeDocument.documentElement.style.setProperty('--content-scale', String(scale));
            this.flow?.setScale(scale);
        });
    }
    navigate(location, options = {}) {
        console.log('Navigating to', location);
        options.behavior ||= 'smooth';
        if (location.pageNumber) {
            options.block ||= 'start';
            let range;
            if (location.pageNumber.startsWith('epubcfi(')) {
                range = this.getRange(location.pageNumber, true);
            }
            else {
                if (this.flow.startRange && this.pageMapping.getPageLabel(this.flow.startRange) === location.pageNumber) {
                    console.log('Already on page', location.pageNumber);
                    return;
                }
                range = this.pageMapping.getRange(location.pageNumber);
            }
            if (!range) {
                console.error('Unable to find range');
                return;
            }
            this.flow.scrollIntoView(range, options);
        }
        else if (location.href) {
            options.block ||= 'start';
            let [pathname, hash] = location.href.split('#');
            try {
                pathname = decodeURIComponent(pathname);
            }
            catch (e) {
                console.warn('Unable to decode pathname', pathname);
            }
            try {
                hash = decodeURIComponent(hash);
            }
            catch (e) {
                console.warn('Unable to decode hash', hash);
            }
            let section = this.book.spine.get(pathname);
            if (!section) {
                console.error('Unable to find section for pathname', pathname);
                return;
            }
            let target = hash && this._sectionRenderers[section.index].container
                .querySelector('[id="' + CSS.escape(hash) + '"]');
            if (target) {
                this.flow.scrollIntoView(target, options);
            }
            else {
                let view = this._sectionRenderers[section.index];
                if (!view) {
                    console.error('Unable to find view for section', section.index);
                    return;
                }
                this.flow.scrollIntoView(view.container, options);
            }
        }
        else {
            super.navigate(location, options);
        }
    }
    navigateToFirstPage() {
        this.flow.navigateToFirstPage();
    }
    navigateToLastPage() {
        this.flow.navigateToLastPage();
    }
    canNavigateToPreviousPage() {
        return this.flow.canNavigateToPreviousPage();
    }
    canNavigateToNextPage() {
        return this.flow.canNavigateToNextPage();
    }
    navigateToPreviousPage() {
        this.flow.navigateToPreviousPage();
    }
    navigateToNextPage() {
        this.flow.navigateToNextPage();
    }
    canNavigateToPreviousSection() {
        return !!this.flow.startSection?.prev();
    }
    canNavigateToNextSection() {
        return !!this.flow.startSection?.next();
    }
    navigateToPreviousSection() {
        let section = this.flow.startSection?.prev();
        if (section) {
            this.navigate({ href: section.href });
        }
    }
    navigateToNextSection() {
        let section = this.flow.startSection?.next();
        if (section) {
            this.navigate({ href: section.href });
        }
    }
    async print() {
        // It's going to get ugly in here, so hide the iframe
        this._iframe.classList.remove('loaded');
        // Mount all views
        let renderersToMount = this._sectionRenderers.filter(view => !view.mounted);
        for (let renderer of renderersToMount) {
            renderer.mount();
        }
        // Wait for all images to load
        await Promise.allSettled(Array.from(this._iframeDocument.images)
            .map(image => image.decode()));
        if (typeof this._iframeWindow.zoteroPrint === 'function') {
            await this._iframeWindow.zoteroPrint();
        }
        else {
            this._iframeWindow.print();
        }
        // Unmount the views that weren't mounted before
        for (let view of renderersToMount) {
            view.unmount();
        }
        this._iframe.classList.add('loaded');
    }
    setSidebarOpen(_sidebarOpen) {
        window.dispatchEvent(new Event('resize'));
    }
    get renderers() {
        return this._sectionRenderers;
    }
    static getContainingSectionIndex(rangeOrNode) {
        let elem;
        if ('nodeType' in rangeOrNode) {
            elem = closestElement(rangeOrNode);
        }
        else {
            elem = closestElement(rangeOrNode.startContainer.childNodes[rangeOrNode.startOffset] || rangeOrNode.startContainer);
        }
        elem = elem?.closest('[data-section-index]');
        if (!elem) {
            return null;
        }
        return parseInt(elem.getAttribute('data-section-index'));
    }
    static compareBoundaryPoints(how, a, b) {
        if (a.startContainer.getRootNode() !== b.startContainer.getRootNode()) {
            let aSectionIndex = this.getContainingSectionIndex(a);
            if (aSectionIndex === null) {
                throw new Error('a is not inside a section');
            }
            let bSectionIndex = this.getContainingSectionIndex(b);
            if (bSectionIndex === null) {
                throw new Error('b is not inside a section');
            }
            if (aSectionIndex === bSectionIndex) {
                return -1;
            }
            return aSectionIndex - bSectionIndex;
        }
        return a.compareBoundaryPoints(how, b);
    }
}
export default EPUBView;
